import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackListingUserEvent, trackSimpleEvent, trackUserEvent } from '../../../client/analytics';
import type { ListingUserEventArgs, UserEventArgs } from '../../../client/analytics';

// https://dataportal.zillowgroup.net/event-registry/4288
const emitOptIntoRentReportingEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '4288',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rent_reporting_optin_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_optin',
    },
    semantic: {
      semantic_event_nm: 'opt_into_rent_reporting',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/4289
const emitSkipOptIntoRentReportingEvent = ({
  user,
  currentProperty,
  fromDashboard,
}: ListingUserEventArgs & {
  fromDashboard: boolean;
}) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '4289',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rent_reporting_optin_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: fromDashboard ? 'dashboard_skip_optin' : 'new_user_skip_optin',
    },
    semantic: {
      semantic_event_nm: 'skip_rent_reporting',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/4290
const emitOpenRentReportingFAQEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '4290',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rent_reporting_optin_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'open_faq_drawer',
    },
    semantic: {
      semantic_event_nm: 'view_faq_rent_reporting',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/4291
const emitOpenRentReportingFAQViewMoreEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '4291',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rent_reporting_optin_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'view_more_faq_modal',
    },
    semantic: {
      semantic_event_nm: 'view_faq_rent_reporting',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/4292
const emitOptIntoRentReportingDashboardEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '4292',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rachel_payment_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_optin',
    },
    semantic: {
      semantic_event_nm: 'opt_into_rent_reporting',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/4293
const emitOptOutOfRentReportingEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '4293',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rachel_payment_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_optout',
    },
    semantic: {
      semantic_event_nm: 'opt_out_rent_reporting',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/4294
const emitOpenRentReportingFAQDashboardEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackListingUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '4294',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rachel_payment_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_view_faqs',
    },
    semantic: {
      semantic_event_nm: 'view_faq_rent_reporting',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7550
const emitClickConfirmDetailsEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7550',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'confirm_details_page',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7611
const emitClickContinueFromPaymentSelectionPageEvent = ({
  user,
  triggerSource,
}: UserEventArgs & { triggerSource: string }) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7611',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_info_method_selection_page',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7608
const emitClickContinueFromCardDetailsPageEvent = ({
  user,
  currentProperty,
  triggerSource,
}: ListingUserEventArgs & { triggerSource: string }) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7608',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_info_card_details_page',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7609
const emitClickContinueFromAddressPageEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7609',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_info_address_page',
      trigger_source_nm: 'continue_billing_address',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7610
const emitClickContinueFromMoveInCostsPageEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7610',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'move_in_costs_page',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7612
const emitClickContinueFromPaymentSettingsCadencePageEvent = ({
  user,
  currentProperty,
  triggerSource,
}: ListingUserEventArgs & { triggerSource: string }) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7612',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_settings_cadence_page',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    // zrm_listing_info
  });

// https://dataportal.zillowgroup.net/event-registry/7613
const emitClickContinueFromPaymentSettingsSchedulingPageEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7613',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_settings_scheduling_page',
      trigger_source_nm: 'continue_date_selection',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7614
const emitClickContinueFromPaymentSettingsConfirmationPageEvent = ({ user, currentProperty }: ListingUserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7614',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'payment_settings_confirmation_page',
      trigger_source_nm: 'continue',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7617
const emitClickGoToDashboardEvent = ({
  user,
  currentProperty,
  triggerObject,
}: ListingUserEventArgs & { triggerObject: string }) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7617',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'click_go_to_dashboard',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
  });

// https://dataportal.zillowgroup.net/event-registry/7615
const emitClickFinishLaterEvent = ({ user, triggerObject }: UserEventArgs & { triggerObject: string }) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7615',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'finish_later_button',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7616
const emitClickBackButtonEvent = ({ user, triggerObject }: UserEventArgs & { triggerObject: string }) =>
  trackUserEvent({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: '7616',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'renter_payments_onboarding',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'button_to_go_back',
    },
    semantic: {
      semantic_event_nm: 'continue_renter_onboarding',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7802
const emitStartCreditTrackingFromOverviewEvent = () =>
  trackSimpleEvent({
    envelope: {
      event_template_id: '171',
      event_template_version_id: '1',
      event_type_id: '7802',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rachel_payment_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'renter_dashboard',
      trigger_source_nm: 'start_tracking',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: [],
    },
  });

// https://dataportal.zillowgroup.net/event-registry/7683
const emitGetStartedTrackingEvent = () =>
  trackSimpleEvent({
    envelope: {
      event_template_id: '171',
      event_template_version_id: '1',
      event_type_id: '7683',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rachel_payment_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rent_reporting_tab',
      trigger_source_nm: 'start_tracking',
    },
    semantic: {
      semantic_event_nm: 'start_tracking_credit',
      topic_tag_txt: [],
    },
  });

// https://dataportal.zillowgroup.net/event-registry/7684
const emitOptOutCreditTrackingEvent = () =>
  trackSimpleEvent({
    envelope: {
      event_template_id: '171',
      event_template_version_id: '1',
      event_type_id: '7684',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rachel_payment_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rent_reporting_tab',
      trigger_source_nm: 'opt_out',
    },
    semantic: {
      semantic_event_nm: 'opt_out_rent_reporting',
      topic_tag_txt: [],
    },
  });

// https://dataportal.zillowgroup.net/event-registry/7685
const emitFaqCreditTrackingEvent = (faqId: string) =>
  trackSimpleEvent({
    envelope: {
      event_template_id: '171',
      event_template_version_id: '1',
      event_type_id: '7685',
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'rachel_payment_dashboard',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'rent_reporting_tab',
      trigger_source_nm: faqId,
    },
    semantic: {
      semantic_event_nm: 'view_faq_rent_reporting',
      topic_tag_txt: [],
    },
  });

// https://dataportal.zillowgroup.net/event-registry/8405
export const emitClickSendFromMoveInCostsPageDashboardFlowEvent = ({
  trigger_source,
  user,
}: UserEventArgs & {
  trigger_source: string;
}) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8405',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: trigger_source,
      trigger_object_nm: 'move_in_costs_page',
    },
    semantic: {
      semantic_event_nm: 'make_mic_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8406
export const emitClickSchedulePaymentFromDashboardEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8406',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'schedule_payment_button',
      trigger_object_nm: 'renter_dashboard',
    },
    semantic: {
      semantic_event_nm: 'make_mic_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8407
export const emitClickContinueFromSelectPaymentMethodModalEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8407',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'select_payment_method_continue_button',
      trigger_object_nm: 'move_in_costs_page',
    },
    semantic: {
      semantic_event_nm: 'make_mic_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8408
export const emitClickContinueFromCadencePageEvent = ({
  user,
  triggerSource,
}: UserEventArgs & { triggerSource: string }) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8408',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: triggerSource,
      trigger_object_nm: 'payment_settings_cadence_page',
    },
    semantic: {
      semantic_event_nm: 'make_rent_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8409
export const emitClicksContinueFromSchedulingPageEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8409',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'continue_date_selection',
      trigger_object_nm: 'payment_settings_scheduling_page',
    },
    semantic: {
      semantic_event_nm: 'make_rent_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8410
export const emitClicksScheduleRecurringRentPaymentEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8410',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'continue',
      trigger_object_nm: 'payment_settings_confirmation_page',
    },
    semantic: {
      semantic_event_nm: 'make_rent_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8411
export const emitClicksContinueInSelectPaymentModalEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8411',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'select_payment_method_continue_button',
      trigger_object_nm: 'select_payment_method_page',
    },
    semantic: {
      semantic_event_nm: 'make_rent_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8412
export const emitClicksScheduleRentPaymentFromDashboardEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8412',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'schedule_rent_payment_now_button',
      trigger_object_nm: 'renter_dashboard',
    },
    semantic: {
      semantic_event_nm: 'make_rent_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8413
export const emitClicksStartOTPPaymentEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8413',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'start_payment_button',
      trigger_object_nm: 'renter_dashboard',
    },
    semantic: {
      semantic_event_nm: 'make_otp_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8414
export const emitClicksSendNowForOTPEvent = ({ user }: UserEventArgs) =>
  trackUserEvent({
    envelope: {
      event_type_id: '8414',
      event_type_version_id: '1',
      event_template_id: '167',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'renter_payments_dashboard',
      trigger_source_nm: 'send_now_button',
      trigger_object_nm: 'otp_page',
    },
    semantic: {
      semantic_event_nm: 'make_otp_payment',
      topic_tag_txt: ['renter_payments'],
    },
    ...getZrmUserInfo(user),
  });

export const RachelCSEvents = {
  emitOptIntoRentReportingEvent,
  emitSkipOptIntoRentReportingEvent,
  emitOpenRentReportingFAQEvent,
  emitOpenRentReportingFAQViewMoreEvent,
  emitOptIntoRentReportingDashboardEvent,
  emitOptOutOfRentReportingEvent,
  emitOpenRentReportingFAQDashboardEvent,
  emitClickConfirmDetailsEvent,
  emitClickContinueFromPaymentSelectionPageEvent,
  emitClickContinueFromCardDetailsPageEvent,
  emitClickContinueFromAddressPageEvent,
  emitClickContinueFromMoveInCostsPageEvent,
  emitClickContinueFromPaymentSettingsCadencePageEvent,
  emitClickContinueFromPaymentSettingsSchedulingPageEvent,
  emitClickContinueFromPaymentSettingsConfirmationPageEvent,
  emitClickGoToDashboardEvent,
  emitClickFinishLaterEvent,
  emitClickBackButtonEvent,
  emitStartCreditTrackingFromOverviewEvent,
  emitGetStartedTrackingEvent,
  emitOptOutCreditTrackingEvent,
  emitFaqCreditTrackingEvent,
  emitClickSendFromMoveInCostsPageDashboardFlowEvent,
  emitClickSchedulePaymentFromDashboardEvent,
  emitClickContinueFromSelectPaymentMethodModalEvent,
  emitClickContinueFromCadencePageEvent,
  emitClicksContinueFromSchedulingPageEvent,
  emitClicksScheduleRecurringRentPaymentEvent,
  emitClicksContinueInSelectPaymentModalEvent,
  emitClicksScheduleRentPaymentFromDashboardEvent,
  emitClicksStartOTPPaymentEvent,
  emitClicksSendNowForOTPEvent,
};

export default RachelCSEvents;
